import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { 
  BookOpen, Menu, X, Users 
} from 'lucide-react';

const NAV_ITEMS = [
  { name: 'Beranda', href: '/' },
  { name: 'Paket', href: '/packages' },
  { name: 'Try Out', href: '/tryouts' },
  { name: 'Ranking', href: '/ranking' },
  { name: 'Video', href: '/video' },
  { name: 'Program', href: '/programs' },
];

export function Header({ showStats = false, stats = null }) {
  const { user } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isActive = (href) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  return (
    <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200/50">
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
          <img src="/logo-mytentor.png" alt="MyTentor Logo" className="w-10 h-10 object-contain" onError={(e) => {
            e.target.onerror = null;
            e.target.style.display = 'none';
            e.target.parentElement.querySelector('.fallback-logo')?.classList.remove('hidden');
          }} />
          <div className="w-9 h-9 bg-[#4472C4] rounded-lg items-center justify-center fallback-logo hidden">
            <BookOpen className="w-5 h-5 text-white" />
          </div>
          <span className="text-lg font-bold text-slate-900 tracking-tight">Bimbel My Tentor</span>
        </Link>
        
        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-1">
          {NAV_ITEMS.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                isActive(item.href) 
                  ? 'bg-[#4472C4] text-white' 
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              {item.name}
            </Link>
          ))}
        </nav>

        {/* Right side - Stats & Auth */}
        <div className="flex items-center gap-3">
          {showStats && stats && (
            <div className="hidden md:flex items-center gap-2 text-sm text-slate-600">
              <Users className="w-4 h-4" />
              <span>{stats.users || '3,000+'}</span>
            </div>
          )}
          
          {user ? (
            <Link to={user.role === 'admin' ? '/admin' : '/dashboard'}>
              <Button className="bg-[#4472C4] hover:bg-[#3861a8] rounded-lg text-sm" data-testid="dashboard-btn">
                <span className="hidden sm:inline">{user.name}</span>
                <span className="sm:hidden">Dashboard</span>
              </Button>
            </Link>
          ) : (
            <Link to="/login">
              <Button variant="outline" className="rounded-lg text-sm" data-testid="login-btn">
                Masuk
              </Button>
            </Link>
          )}

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6 text-slate-600" />
            ) : (
              <Menu className="w-6 h-6 text-slate-600" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden border-t border-slate-200 bg-white">
          <nav className="max-w-7xl mx-auto px-4 py-3 space-y-1">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                  isActive(item.href) 
                    ? 'bg-[#4472C4] text-white' 
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
                data-testid={`mobile-nav-${item.name.toLowerCase().replace(' ', '-')}`}
              >
                {item.name}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}

export default Header;
